/**
 * What each valuation method is, fetched once and shared.
 *
 * The text is AUTHORED on the backend, in `app/valuation/v2/explanations.py`, beside the
 * methods it describes. Held there rather than here for one reason: a method added to
 * the enum without an explanation is caught by a backend test, and a copy in the
 * frontend would drift the first time somebody added the ninth supplied method.
 *
 * Fetched ONCE per session and cached in a module-level promise. It is static text that
 * cannot change between two calls in the same session, so a per-component fetch would
 * make one request per panel render for an answer that never differs.
 */

import { useEffect, useState } from 'react';
import { useAuthStore } from '../../../store/auth';

export interface MethodExplanation {
  /** What it measures. */
  whatItIs: string;
  /** The arithmetic in words — complements `basis`, which has the real figures in it. */
  howItWorks: string;
  /** When to distrust this method's answer. Never empty, on purpose. */
  misleads: string;
}

export type Explanations = Record<string, MethodExplanation>;

/**
 * The in-flight or completed fetch.
 *
 * A promise rather than the value, so two components mounting together share one
 * request instead of racing to make two.
 */
let pending: Promise<Explanations> | null = null;

export function loadExplanations(): Promise<Explanations> {
  if (pending) return pending;
  pending = (async () => {
    try {
      const token = useAuthStore.getState().token;
      const response = await fetch('/api/v2/models/valuation/method-explanations', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(String(response.status));
      return (await response.json()) as Explanations;
    } catch {
      // Explanations are commentary. A panel that failed to load them should show the
      // figures without them rather than show an error where a valuation belongs - and
      // the next mount may as well try again.
      pending = null;
      return {};
    }
  })();
  return pending;
}

export function useExplanations(): Explanations {
  const [explanations, setExplanations] = useState<Explanations>({});

  useEffect(() => {
    let cancelled = false;
    void loadExplanations().then((loaded) => {
      if (!cancelled) setExplanations(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return explanations;
}
